import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import styled from "styled-components";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";

type DeviceRow = {
  deviceId: string;
  shortId?: string;
  status: string;
  owner?: string | { username?: string };
  panelProfile?: string;
  layoutRevision?: number;
  lastSeen?: string | null;
};

const Wrap = styled.div`
  min-height: calc(100vh - 64px);
  background: #f0f0f0;
  padding: 1.25rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  @media (max-width: 700px) {
    padding: 12px 8px;
  }
`;

const Card = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px #232323;
  padding: 1.25rem;
  @media (max-width: 700px) {
    padding: 1rem 0.85rem;
  }
`;

const Title = styled.h2`
  margin: 0 0 1rem;
  font-size: 1.25rem;
  letter-spacing: -0.02em;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 700px) {
    margin: 0 -8px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  th,
  td {
    border: 1px solid #232323;
    padding: 0.45rem 0.5rem;
    text-align: left;
    vertical-align: middle;
  }
  th {
    background: #fafafa;
    font-weight: 700;
  }
  tbody tr:nth-child(even) {
    background: #fafafa;
  }
  @media (max-width: 700px) {
    min-width: 640px;
  }
`;

const BtnLink = styled(Link)`
  display: inline-block;
  padding: 0.35rem 0.65rem;
  border: 1px solid #232323;
  background: #fff;
  color: #232323;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.8rem;
  box-shadow: 3px 3px #232323;
  &:hover {
    background: #e8e8e8;
  }
`;

const Err = styled.p`
  color: crimson;
  margin: 0 0 0.75rem;
`;

export default function AotdIrlAdminDevicesList() {
  const auth = useAppSelector((s) => s.auth);
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get("/api/aotd-irl/admin/devices").catch(async () => {
          return apiClient.get("/api/aotd-irl/devices");
        });
        if (!cancelled) setDevices(resp.data?.devices ?? []);
      } catch (e: unknown) {
        const msg = e && typeof e === "object" && "message" in e ? String((e as Error).message) : "failed to load";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  if (!checkRole(auth.role ?? "", "admin")) {
    return <Navigate to="/" replace />;
  }

  return (
    <Wrap>
      <Card>
        <Title>AOTD-IRL devices</Title>
        {loading && <p style={{ margin: 0 }}>Loading…</p>}
        {error && <Err>{error}</Err>}
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>Short</th>
                <th>Device ID</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Panel</th>
                <th>Rev</th>
                <th>Last seen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr key={d.deviceId}>
                  <td>{d.shortId ?? "—"}</td>
                  <td>
                    <code>{d.deviceId}</code>
                  </td>
                  <td>{typeof d.owner === "object" ? d.owner?.username ?? "—" : d.owner ?? "—"}</td>
                  <td>{d.status}</td>
                  <td>{d.panelProfile ?? "—"}</td>
                  <td>{d.layoutRevision ?? "—"}</td>
                  <td>{d.lastSeen ? new Date(d.lastSeen).toLocaleString() : "—"}</td>
                  <td>
                    <BtnLink
                      style={{ marginRight: "0.35rem" }}
                      to={`/aotd-irl/admin/devices/${encodeURIComponent(d.deviceId)}`}
                    >
                      Manage
                    </BtnLink>
                    <BtnLink to={`/aotd-irl/admin/devices/${encodeURIComponent(d.deviceId)}/layout`}>
                      Edit layout
                    </BtnLink>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>
    </Wrap>
  );
}
